import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { CreateBrokerNote } from '../routes/BrokerNote/types/create-broker-note.type';
import { IncomePayload } from '../routes/BrokerNoteGenerator/components/IncomeTable';
import { BrokerNotePromptType } from '../routes/BrokerNote/types/note-name.type';

interface FundsToComplete {
  'Purchase Price': string;
  'Less: Loan Amount': string;
  'Less: Cash Out Amount': string;
  'Add: Stamp Duty, misc.': string;
  'Funds Required': string;
}

export interface FundsAvailable {
  TOTAL: string | number;
  [accountName: string]: string | number;
}
export interface BrokerNoteGsheetData {
  Name1: string;
  Citizenship1: string;
  Residency1: string;
  Occupation1: string;
  Employment1: string;
  Employer1: string;
  Since1: string;
  Currency1: string;
  FX1: string;
  Salary1: string;
  Deductions1: string;
  Allowances1: string;
  Commission1: string;
  BonusThis: string;
  BonusLast: string;
  Number1: string;
  'Any Liabilities': string;
  'Details of debt': string;
  Date: string;
  Name2: string;
  Citizenship2: string;
  Residency2: string;
  Occupation2: string;
  Employment2: string;
  Employer2: string;
  Since2: string;
  Currency2: string;
  FX2: string;
  Salary2: string;
  Deductions2: string;
  Allowances2: string;
  Commission2: string;
  BonusThis2: string;
  BonusLast2: string;
  Number2: string;
  Pricing: string;
  'Interest Rate': string;
  Valuation: string;
  'Living status': string;
  'Mortgage?': string;
  'Rental expense': string;
  'Rental expense AUD': string;
  'Marital status': string;
  Kids_number: string;
  Kids_ages: string;
  'Private education': string;
  Purpose: string;
  PurposeEquity: string;
  LVR: string;
  Security: string;
  'AIP rental yield': string;
  'AIP rent /m': string;
  'C/C total limit': string;
  'Reduce limit?': string;
  'CC New limit': string;
  Lender: string;
  'Existing Customer': string;
  fundsToComplete: FundsToComplete;
  fundsAvailable: FundsAvailable;
}

export interface PromptDto {
  id: number;
  type: BrokerNotePromptType;
  prompt: string;
  updatedAt: string;
  createdAt: string;
  version: number | null;
}

export interface UpdatePromptDto {
  prompt: string;
}

export const brokerNoteApi = createApi({
  reducerPath: 'brokerNoteApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Prompts'],
  endpoints: (builder) => ({
    getBrokerNoteDataBySheetId: builder.query<
      ApiResponse<BrokerNoteGsheetData>,
      string
    >({
      query: (sheetId) => {
        return {
          url: `/v1/broker-note/sheet-data/${sheetId}`,
        };
      },
    }),
    getBrokerNotePrompts: builder.query<
      Record<BrokerNotePromptType, PromptDto>,
      void
    >({
      query: () => '/v1/broker-note/prompts',
      providesTags: () => [{ type: 'Prompts', id: 'LIST' }],
      transformResponse: (res: ApiResponse<PromptDto[]>) =>
        res.data.reduce(
          (acc, p) => {
            acc[p.type] = p;
            return acc;
          },
          {} as Record<BrokerNotePromptType, PromptDto>,
        ),
    }),

    updateBrokerNotePrompt: builder.mutation<
      PromptDto,
      { type: BrokerNotePromptType; prompt: string }
    >({
      query: ({ type, prompt }) => ({
        url: `/v1/broker-note/update/prompt/${type}`,
        method: 'PATCH',
        body: { prompt },
      }),
      invalidatesTags: () => [{ type: 'Prompts', id: 'LIST' }],
    }),
    generateBrokerNote: builder.mutation<
      ApiResponse<any>,
      { userContent: string; noteCategory: string }
    >({
      query: (body) => {
        return {
          url: '/v1/broker-note/generate',
          method: 'POST',
          body,
        };
      },
    }),
    createBrokerNote: builder.mutation<ApiResponse<any>, CreateBrokerNote>({
      query: (body) => ({
        url: 'v1/broker-note',
        method: 'POST',
        body,
      }),
    }),
    updateBrokerNote: builder.mutation<
      ApiResponse<any>,
      { id: string; payload: Partial<CreateBrokerNote> }
    >({
      query: (body) => ({
        url: `v1/broker-note/${body.id}`,
        method: 'PATCH',
        body: body.payload,
      }),
    }),

    getBrokerNoteDetail: builder.query<
      ApiResponse<any>,
      { serviceName: string; serviceId: string }
    >({
      query: (objectType) =>
        `v1/broker-note/by-service/lookup?serviceName=${objectType.serviceName}&serviceId=${objectType.serviceId}`,
    }),

    getIncomeDetails: builder.query<ApiResponse<IncomePayload[]>, string>({
      query: (sheetId) => `v1/broker-note/sheet-data/calcs/lenders/${sheetId}`,
    }),
  }),
});

export const {
  useGetBrokerNoteDataBySheetIdQuery,
  useGetBrokerNotePromptsQuery,
  useGenerateBrokerNoteMutation,
  useCreateBrokerNoteMutation,
  useUpdateBrokerNoteMutation,
  useGetBrokerNoteDetailQuery,
  useGetIncomeDetailsQuery,

  useUpdateBrokerNotePromptMutation,
} = brokerNoteApi;
